import { useEffect, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { House, Key } from "@phosphor-icons/react";
import WatchButton from "@/components/WatchButton";
import PdfButton from "@/components/PdfButton";
import CityCombobox from "@/components/CityCombobox";

export default function Inserzioni() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("milano");
  const [type, setType] = useState("vendita");
  const [items, setItems] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minSqm, setMinSqm] = useState("");
  const [source, setSource] = useState("");
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/sources").then((r) => setSources(r.data.listings || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ city, type, limit: "200" });
    if (minPrice) params.append("min_price", minPrice);
    if (maxPrice) params.append("max_price", maxPrice);
    if (minSqm) params.append("min_sqm", minSqm);
    if (source) params.append("source", source);
    api.get(`/listings?${params.toString()}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [city, type, minPrice, maxPrice, minSqm, source]);

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— INSERZIONI RESIDENZIALI</div>
          <h1 className="font-display font-black text-3xl mb-6">Vendita & Affitto</h1>

          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[220px]">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CITTÀ</Label>
              <div className="mt-1">
                <CityCombobox value={city} onChange={setCity} cities={cities} testId="listings-city-select" />
              </div>
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">TIPO</Label>
              <Tabs value={type} onValueChange={setType} className="mt-1">
                <TabsList className="rounded-none bg-zinc-900 border border-white/10 p-0 h-10">
                  <TabsTrigger value="vendita" className="rounded-none data-[state=active]:bg-lime-400 data-[state=active]:text-lime-950 px-4 font-mono-data text-xs" data-testid="tab-vendita">
                    <House size={12} weight="bold" className="mr-1.5" /> VENDITA
                  </TabsTrigger>
                  <TabsTrigger value="affitto" className="rounded-none data-[state=active]:bg-cyan-500 data-[state=active]:text-cyan-950 px-4 font-mono-data text-xs" data-testid="tab-affitto">
                    <Key size={12} weight="bold" className="mr-1.5" /> AFFITTO
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <div className="w-32">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">PREZZO MIN</Label>
              <Input type="number" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10 font-mono-data text-sm" data-testid="input-min-price" />
            </div>
            <div className="w-32">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">PREZZO MAX</Label>
              <Input type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10 font-mono-data text-sm" data-testid="input-max-price" />
            </div>
            <div className="w-28">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">M² MIN</Label>
              <Input type="number" value={minSqm} onChange={(e) => setMinSqm(e.target.value)} className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10 font-mono-data text-sm" data-testid="input-min-sqm" />
            </div>
            <div className="min-w-[180px]">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">FONTE</Label>
              <Select value={source || "_all"} onValueChange={(v) => setSource(v === "_all" ? "" : v)}>
                <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid="listings-source-select"><SelectValue placeholder="Tutte" /></SelectTrigger>
                <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                  <SelectItem value="_all">Tutte le fonti</SelectItem>
                  {sources.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="ml-auto font-mono-data text-xs text-zinc-500" data-testid="listings-count">
              {loading ? "..." : `${items.length} risultati`}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="listings-grid">
          {items.map((l) => (
            <div key={l.id} className="border border-white/10 bg-zinc-900/60 p-4 hover:border-lime-400/50 transition-colors" data-testid={`listing-card-${l.id}`}>
              <div className="flex items-start justify-between mb-2">
                <Badge className={`rounded-none font-mono-data text-[10px] font-bold ${l.type === "vendita" ? "bg-lime-400 text-lime-950 hover:bg-lime-400" : "bg-cyan-500 text-cyan-950 hover:bg-cyan-500"}`}>
                  {l.type.toUpperCase()}
                </Badge>
                <div className="flex items-center gap-1.5">
                  <Badge className="rounded-none bg-zinc-800 text-zinc-300 font-mono-data text-[9px] hover:bg-zinc-800">{l.source}</Badge>
                  <span className="font-mono-data text-[10px] text-zinc-500">{l.energy_class}</span>
                </div>
              </div>
              <div className="font-display font-bold text-base leading-tight mb-1">{l.title}</div>
              <div className="text-xs text-zinc-500 mb-3">{l.address} · {l.zone}</div>
              <div className="font-mono-data text-xl font-bold text-lime-400">
                {formatEuro(l.price)}{l.type === "affitto" && <span className="text-xs text-zinc-500">/mese</span>}
              </div>
              <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-3 gap-2 font-mono-data text-xs text-zinc-400">
                <div><div className="text-[10px] text-zinc-600">M²</div>{l.sqm}</div>
                <div><div className="text-[10px] text-zinc-600">LOCALI</div>{l.rooms}</div>
                <div><div className="text-[10px] text-zinc-600">€/M²</div>{Math.round(l.price_per_sqm)}</div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <WatchButton kind="listing" id={l.id} city={l.city} />
                <PdfButton kind="property" id={l.id} />
              </div>
            </div>
          ))}
        </div>
        {!loading && items.length === 0 && (
          <div className="text-center py-12 font-mono-data text-sm text-zinc-500">Nessun risultato per i filtri selezionati</div>
        )}
      </div>
    </div>
  );
}
